import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Grid, Checkbox } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./procedure.module.css";
import {
  useGetProcedureDetailQuery,
  useGetSpecialitiesQuery,
  useUpdateProcedureDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

/**
 * Procedure details view with speciality mapping and deactivation info.
 *
 * Notes:
 * - Displays speciality names based on ids from the procedure detail.
 * - TODO: Confirm desired empty/error state behavior when detail fetch fails.
 */
const ProcedureDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [specialities, setSpecialities] = useState<Speciality[]>([]);
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    data: procedureDetails,
    isLoading,
    isFetching,
  } = useGetProcedureDetailQuery(id ?? "");
  const [updateProcedureDetails] = useUpdateProcedureDetailsMutation();

  const { data: specialityData, isLoading: isSpecialitiesLoading } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: false,
    search_key: ""
  });

  useEffect(() => {
    if (specialityData && procedureDetails) {
      // Translate stored ids into display-ready records; missing ids are ignored.
      const mappedSpecialities = procedureDetails?.speciality_ids
        ?.map((specialityId) =>
          specialityData?.items?.find(
            (speciality: Speciality) => speciality.id === specialityId
          )
        )
        .filter((speciality): speciality is Speciality => Boolean(speciality));
      setSpecialities(mappedSpecialities ?? []);
    }
  }, [specialityData, procedureDetails]);

  const handleActivate = async () => {
    if (!procedureDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateProcedureDetails({
        is_active: true,
        id: procedureDetails.id,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading || isFetching || isSpecialitiesLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "15px 40px" }}>
            <Typography className={style.procedureDetailHeading}>
              Procedure Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Name</Typography>
                <Typography className={style.value}>
                  {procedureDetails?.name}
                </Typography>
              </Box>
            </Box>
            <Box
              className={style.detailsRow}
              sx={{ alignItems: "flex-start !important" }}
            >
              <Typography className={style.title}>Specialities</Typography>
              <Box className={style.value}>
                <Grid container columnSpacing={3}>
                  {specialities?.map((speciality: Speciality) => {
                    return (
                      <Grid
                        item
                        xs={specialities?.length > 10 ? 6 : 12}
                        key={speciality?.id}
                      >
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          paddingY={1}
                        >
                          <Checkbox
                            checked={true}
                            disableTouchRipple
                            sx={{
                              transform: "scale(0.92)",
                              padding: "0px",
                            }}
                            disabled
                          />
                          <Typography sx={{ marginLeft: 1 }}>
                            {speciality?.name ?? ""}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
            {procedureDetails?.deactivate_reason && (
              <Box>
                <Typography className={style.procedureDetailHeading}>
                  Deactivation Details
                </Typography>
                <Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Reason for deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {procedureDetails?.deactivate_reason}
                    </Typography>
                  </Box>
                  <Box className={style.detailsRow}>
                    <Typography className={style.title}>
                      Date of Deactivation
                    </Typography>
                    <Typography className={style.value}>
                      {procedureDetails?.deactivated_at
                        ? dayjs(procedureDetails?.deactivated_at).format(
                          "MMMM DD YYYY, hh:mm:ss A"
                        )
                        : "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: "transparent" },
                }}
                variant="outlined"
                onClick={() => navigate(-1)}
                label={"Close"}
              />
              {procedureDetails?.is_active ? (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Edit"}
                  onClick={() =>
                    navigate(`/procedures/edit/${procedureDetails?.id}`)
                  }
                />
              ) : (
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: colors.primary },
                  }}
                  variant="contained"
                  label={"Activate"}
                  onClick={() => setActivateModalVisible(true)}
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${procedureDetails?.name}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box >
  );
}

export default ProcedureDetails;
